import { type ComputedRef, type Ref, computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import type {
  ComparisonProduct,
  ProductSpecification,
  Scores,
} from '@backmarket/nuxt-layer-recommendation/models/product'

import {
  PRODUCT_COMPARISON_CAMERA_CAT_INFOS,
  PRODUCT_COMPARISON_CAMERA_FIELDS,
  PRODUCT_COMPARISON_DURABILITY_CAT_INFOS,
  PRODUCT_COMPARISON_DURABILITY_FIELDS,
  PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
  PRODUCT_COMPARISON_MULTIMEDIA_CAT_INFOS,
  PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
  PRODUCT_COMPARISON_PERFORMANCE_CAT_INFOS,
  PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
  PRODUCT_COMPARISON_SCREEN_QUALITY_CAT_INFOS,
  PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
  PRODUCT_COMPARISON_SOUND_QUALITY_CAT_INFOS,
  PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
} from '../components/ProductComparison/ProductComparison.constants'
import {
  cleanTableData,
  getScoreField,
  getSpecificationField,
  getSpecificationFields,
} from '../helpers/comparisonHelper'
import type { Line, TableData } from '../models/productComparison.ts'

import translations from './useProductComparisonModal.translations'

export function useProductComparisonModal({
  products,
  productsSpecifications,
}: {
  products: Ref<ComparisonProduct[]>
  productsSpecifications: Ref<ProductSpecification[][]>
}) {
  const i18n = useI18n()

  const categoryId = computed(() => products.value.map((i) => i.categoryId)[0])

  function getInfoField(categoryInfos: { [key: number]: string }): Line {
    return {
      type: 'info',
      value:
        categoryId.value && categoryInfos[categoryId.value]
          ? i18n(
              translations[
                categoryInfos[categoryId.value] as keyof typeof translations
              ],
            )
          : '',
    }
  }

  const productsScores: ComputedRef<Scores[]> = computed(
    () => products.value?.map((p) => p.scores || {}) || [],
  )

  const tableData = computed<TableData[]>(() => {
    if (!products.value) return []

    const res: TableData[] = [
      {
        sections: [
          {
            lines: [
              {
                type: 'ratings',
                title: i18n(translations.ratings),
                values: products.value.map(
                  (product) => product.reviewRating?.average || 0,
                ),
              },
              {
                type: 'colors',
                title: i18n(translations.colorOptions),
                values: products.value.map(
                  (product) => product.specifications?.color || [],
                ),
              },
              {
                type: 'tags',
                title: i18n(translations.storageOptions),
                values: products.value.map(
                  (product) =>
                    product.specifications?.storage?.map((i) => i.name) || [],
                ),
              },
            ],
          },
        ],
      },
      {
        title: i18n(translations.scores),
        sections: [
          {
            title: i18n(translations.durability),
            trackingKey: 'comparison_tool_durability',
            lines: [
              getScoreField(productsScores.value, 'lifeExpectancy'),
              getInfoField(PRODUCT_COMPARISON_DURABILITY_CAT_INFOS),
              ...PRODUCT_COMPARISON_DURABILITY_FIELDS.map((field) =>
                getSpecificationField(productsSpecifications.value, field),
              ),
            ],
          },
          {
            title: i18n(translations.performance),
            trackingKey: 'comparison_tool_performance',
            lines: [
              getScoreField(productsScores.value, 'performances'),
              getInfoField(PRODUCT_COMPARISON_PERFORMANCE_CAT_INFOS),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.screenQuality),
            trackingKey: 'comparison_tool_screen_quality',
            lines: [
              getScoreField(productsScores.value, 'screenQuality'),
              getInfoField(PRODUCT_COMPARISON_SCREEN_QUALITY_CAT_INFOS),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.soundQuality),
            trackingKey: 'comparison_tool_sound_quality',
            lines: [
              getScoreField(productsScores.value, 'soundQuality'),
              getInfoField(PRODUCT_COMPARISON_SOUND_QUALITY_CAT_INFOS),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.multimedia),
            trackingKey: 'comparison_tool_multimedia',
            lines: [
              getScoreField(productsScores.value, 'multimedia'),
              getInfoField(PRODUCT_COMPARISON_MULTIMEDIA_CAT_INFOS),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.camera),
            trackingKey: 'comparison_tool_camera',
            lines: [
              getScoreField(productsScores.value, 'camera'),
              getInfoField(PRODUCT_COMPARISON_CAMERA_CAT_INFOS),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_CAMERA_FIELDS,
              ),
            ],
          },
        ],
      },
      {
        title: i18n(translations.miscelaneous),
        separator: true,
        sections: [
          {
            trackingKey: 'comparison_tool_miscelaneous',
            lines: [
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
              ),
            ],
          },
        ],
      },
    ]

    return cleanTableData(res)
  })

  return {
    tableData,
  }
}
